import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  useColorModeValue,
  Spinner,
  Checkbox,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Code,
  IconButton,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import AdminLayout from '../../layouts/AdminLayout';
import { ErrorEvent, ErrorListResponse, getError, getErrors, getExternalError, getExternalErrors } from '../../services/errors';
import { RepeatIcon } from '@chakra-ui/icons';
import { getAdminSettings } from '../../services/settings';
import DateRangePicker from '../../components/common/DateRangePicker';

const useAutoRefresh = (enabled: boolean, tickMs: number, onTick: () => void) => {
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(onTick, tickMs);
    return () => clearInterval(id);
  }, [enabled, tickMs, onTick]);
};

const Row: React.FC<{ ev: ErrorEvent; onOpenDetail: (id: number) => void }> = ({ ev, onOpenDetail }) => {
  const color = ev.severity === 'fatal' ? 'red' : ev.severity === 'warn' ? 'yellow' : 'gray';
  let tags: any = (ev as any).tags;
  try { if (typeof tags === 'string') tags = JSON.parse(tags); } catch {}
  const isSupport = tags && tags.type === 'support';
  return (
    <Tr _hover={{ bg: useColorModeValue('gray.50', 'gray.700'), cursor: 'pointer' }} onClick={() => onOpenDetail(ev.id)}>
      <Td whiteSpace="nowrap">{new Date(ev.occurred_at || ev.created_at).toLocaleString()}</Td>
      <Td><Badge colorScheme={color}>{ev.severity || 'error'}</Badge></Td>
      <Td>{ev.origin}</Td>
      <Td>
        <Text noOfLines={1} maxW="420px" title={ev.message}>{ev.message}</Text>
      </Td>
      <Td>{ev.method}</Td>
      <Td><Text noOfLines={1} maxW="260px" title={ev.url}>{ev.url}</Text></Td>
      <Td>{ev.status || ''}</Td>
      <Td>
        <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
          <Code fontSize="xs">{ev.request_id || ''}</Code>
          {ev.request_id ? (
            <Button size="xs" variant="ghost" onClick={async () => { try { await navigator.clipboard.writeText(ev.request_id || ''); } catch {} }}>Kopírovat</Button>
          ) : null}
        </HStack>
      </Td>
      <Td>{isSupport ? <Badge colorScheme="purple">Podpora</Badge> : null}</Td>
    </Tr>
  );
};

const ErrorsAdminPage: React.FC = () => {
  const [origin, setOrigin] = useState('');
  const [severity, setSeverity] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const detail = useDisclosure();
  const [useExternal, setUseExternal] = useState(false);
  const [extUI, setExtUI] = useState<string>('');
  const [supportOnly, setSupportOnly] = useState(false);

  const effectiveSearch = useMemo(() => supportOnly ? ((search && search.trim()) ? `Support: ${search.trim()}` : 'Support:') : search, [supportOnly, search]);
  const effectiveSeverity = useMemo(() => supportOnly ? (severity || 'warn') : severity, [supportOnly, severity]);
  const effectiveOrigin = useMemo(() => supportOnly ? (origin || 'frontend') : origin, [supportOnly, origin]);
  const params = useMemo(() => ({ origin: effectiveOrigin, severity: effectiveSeverity, method, status: status || undefined, search: effectiveSearch, from, to, page, limit }), [effectiveOrigin, effectiveSeverity, method, status, effectiveSearch, from, to, page, limit]);

  const query = useQuery<ErrorListResponse>({
    queryKey: ['admin', 'errors', useExternal ? 'external' : 'local', params],
    queryFn: () => (useExternal ? getExternalErrors(params) : getErrors(params)),
    keepPreviousData: true,
    staleTime: 10_000,
  });

  useAutoRefresh(autoRefresh, 10_000, () => query.refetch());

  const [detailData, setDetailData] = useState<ErrorEvent | null>(null);
  useEffect(() => {
    if (!detail.isOpen || selectedId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const d = useExternal ? await getExternalError(selectedId) : await getError(selectedId);
        if (!cancelled) setDetailData(d);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [detail.isOpen, selectedId, useExternal]);

  useEffect(() => {
    (async () => {
      try {
        const s = await getAdminSettings();
        const u = (s as any)?.error_review_ui_url;
        if (u) setExtUI(u);
      } catch {}
    })();
  }, []);

  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const detailCtxBoxBg = useColorModeValue('gray.50','gray.700');
  const detailCtxBorder = useColorModeValue('gray.200','gray.600');

  return (
    <AdminLayout>
      <Box maxW="1400px" mx="auto">
        <HStack justify="space-between" mb={6} align="center">
          <Heading size="lg">Chyby a výjimky</Heading>
          <HStack>
            <Checkbox isChecked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)}>Auto‑refresh</Checkbox>
            <IconButton aria-label="Refresh" icon={<RepeatIcon />} size="sm" onClick={() => query.refetch()} isLoading={query.isRefetching} />
            <Checkbox isChecked={useExternal} onChange={(e) => setUseExternal(e.target.checked)}>Externí zdroj</Checkbox>
            {extUI ? (
              <Button as="a" href={extUI} target="_blank" rel="noreferrer" size="sm" variant="outline">Konzole</Button>
            ) : null}
          </HStack>
        </HStack>

        <VStack align="stretch" spacing={4} mb={4}>
          <HStack>
            <Select placeholder="Původ (origin)" value={origin} onChange={(e) => setOrigin(e.target.value)} maxW="220px">
              <option value="frontend">frontend</option>
              <option value="backend">backend</option>
              <option value="docker">docker</option>
            </Select>
            <Select placeholder="Závažnost" value={severity} onChange={(e) => setSeverity(e.target.value)} maxW="200px">
              <option value="fatal">fatal</option>
              <option value="error">error</option>
              <option value="warn">warn</option>
            </Select>
            <Select placeholder="Metoda" value={method} onChange={(e) => setMethod(e.target.value)} maxW="160px">
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </Select>
            <Input placeholder="Status" value={status} onChange={(e) => setStatus(e.target.value)} maxW="120px" />
            <Input placeholder="Hledat zpráva/stack/url" value={search} onChange={(e) => setSearch(e.target.value)} />
          </HStack>
          <HStack>
            <DateRangePicker
              from={from ? from.slice(0, 10) : undefined}
              to={to ? to.slice(0, 10) : undefined}
              onChange={(fromValue, toValue) => {
                const fv = (fromValue || '').trim();
                const tv = (toValue || '').trim();
                setFrom(fv ? `${fv}T00:00` : '');
                setTo(tv ? `${tv}T23:59` : '');
                setPage(1);
              }}
              size="sm"
            />
            <Select value={limit} onChange={(e) => setLimit(parseInt(e.target.value || '20', 10))} maxW="140px">
              {[20,50,100,200].map(n => <option key={n} value={n}>{n}/strana</option>)}
            </Select>
            <HStack>
              <Button size="sm" onClick={() => { setPage(1); query.refetch(); }}>Filtr</Button>
              <Button size="sm" variant="outline" onClick={() => { setOrigin(''); setSeverity(''); setMethod(''); setStatus(''); setSearch(''); setFrom(''); setTo(''); setPage(1); setSupportOnly(false); }}>Reset</Button>
              <Checkbox isChecked={supportOnly} onChange={(e) => setSupportOnly(e.target.checked)}>Pouze podpora</Checkbox>
            </HStack>
          </HStack>
        </VStack>

        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" overflow="hidden">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Čas</Th>
                <Th>Sev.</Th>
                <Th>Původ</Th>
                <Th>Zpráva</Th>
                <Th>Metoda</Th>
                <Th>URL</Th>
                <Th>Status</Th>
                <Th>Request ID</Th>
                <Th>Tagy</Th>
              </Tr>
            </Thead>
            <Tbody>
              {query.isLoading ? (
                <Tr><Td colSpan={9}><HStack><Spinner size="sm" /><Text>Načítám...</Text></HStack></Td></Tr>
              ) : (
                query.data?.items?.length ? (
                  query.data.items.map(ev => <Row key={ev.id} ev={ev} onOpenDetail={(id) => { setSelectedId(id); detail.onOpen(); }} />)
                ) : (
                  <Tr><Td colSpan={9}><Text color="gray.500">Žádné chyby</Text></Td></Tr>
                )
              )}
            </Tbody>
          </Table>
        </Box>

        <HStack justify="space-between" mt={4}>
          <Text color="gray.500">Celkem: {query.data?.total ?? 0}</Text>
          <HStack>
            <Button size="sm" onClick={() => setPage(p => Math.max(1, p-1))} isDisabled={page === 1}>Předchozí</Button>
            <Text>Strana {page}</Text>
            <Button size="sm" onClick={() => setPage(p => p + 1)} isDisabled={(query.data?.items?.length || 0) < limit}>Další</Button>
          </HStack>
        </HStack>

        <HStack mt={3} spacing={2} wrap="wrap">
          <Text fontSize="sm" color="gray.500">Rychlé filtry:</Text>
          <Button size="xs" variant="outline" onClick={() => {
            const now = new Date();
            const start = new Date(); start.setHours(0,0,0,0);
            setFrom(start.toISOString().slice(0,16));
            setTo(now.toISOString().slice(0,16));
            setPage(1);
          }}>Dnes</Button>
          <Button size="xs" variant="outline" onClick={() => {
            const now = new Date();
            const past = new Date(now.getTime() - 24*60*60*1000);
            setFrom(past.toISOString().slice(0,16));
            setTo(now.toISOString().slice(0,16));
            setPage(1);
          }}>24 h</Button>
          <Button size="xs" variant="outline" onClick={() => {
            const now = new Date();
            const past = new Date(now.getTime() - 7*24*60*60*1000);
            setFrom(past.toISOString().slice(0,16));
            setTo(now.toISOString().slice(0,16));
            setPage(1);
          }}>7 dní</Button>
          <Button size="xs" variant="ghost" onClick={() => { setFrom(''); setTo(''); }}>Vymazat</Button>
        </HStack>

        <Drawer isOpen={detail.isOpen} placement="right" onClose={detail.onClose} size="lg">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader>Detail chyby</DrawerHeader>
            <DrawerBody>
              {selectedId == null ? null : detailData ? (
                <VStack align="stretch" spacing={3}>
                  <HStack><Text fontWeight="bold">Čas:</Text><Text>{new Date(detailData.occurred_at || detailData.created_at).toLocaleString()}</Text></HStack>
                  <HStack><Text fontWeight="bold">Původ:</Text><Text>{detailData.origin}</Text></HStack>
                  <HStack><Text fontWeight="bold">Závažnost:</Text><Badge>{detailData.severity || 'error'}</Badge></HStack>
                  <HStack><Text fontWeight="bold">URL:</Text><Text>{detailData.method} {detailData.url}</Text></HStack>
                  <HStack><Text fontWeight="bold">Status:</Text><Text>{detailData.status || ''}</Text></HStack>
                  <HStack><Text fontWeight="bold">Request ID:</Text><Code>{detailData.request_id || ''}</Code></HStack>
                  {(() => {
                    let tags: any = (detailData as any).tags; try { if (typeof tags === 'string') tags = JSON.parse(tags); } catch {}
                    if (!tags) return null;
                    return <HStack><Text fontWeight="bold">Tagy:</Text>{Object.entries(tags).map(([k,v]) => <Badge key={k} colorScheme={k==='type'&&v==='support'?'purple':'gray'}>{String(k)}={String(v)}</Badge>)}</HStack>;
                  })()}
                  {(() => {
                    let ctx: any = (detailData as any).context; try { if (typeof ctx === 'string') ctx = JSON.parse(ctx); } catch {}
                    const ra = ctx?.recentActions;
                    if (!ra) return null;
                    return (
                      <Box>
                        <Text fontWeight="bold" mb={1}>Poslední akce</Text>
                        <Box bg={detailCtxBoxBg} borderWidth="1px" borderColor={detailCtxBorder} borderRadius="md" p={2} maxH="220px" overflowY="auto">
                          {(Array.isArray(ra) ? ra : []).map((a: any, i: number) => (
                            <Text key={i} fontFamily="mono" fontSize="xs">{new Date((a.at)||Date.now()).toLocaleTimeString()} {a.type === 'nav' ? `NAV ${a.path}` : `${(a.method||'').toUpperCase()} ${a.url} ${a.status ?? ''} ${a.ms ? a.ms+'ms' : ''}`}</Text>
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                  <Box>
                    <Text fontWeight="bold" mb={1}>Zpráva</Text>
                    <Code whiteSpace="pre-wrap" width="100%" p={2}>{detailData.message}</Code>
                  </Box>
                  {detailData.stack ? (
                    <Box>
                      <Text fontWeight="bold" mb={1}>Stack</Text>
                      <Code whiteSpace="pre" width="100%" p={2} display="block">{detailData.stack}</Code>
                    </Box>
                  ) : null}
                </VStack>
              ) : (
                <HStack><Spinner size="sm" /><Text>Načítání…</Text></HStack>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>
    </AdminLayout>
  );
};

export default ErrorsAdminPage;
